"use client";

import { useState, useMemo, useCallback, useEffect } from "react";
import type { PaymentPageData } from "@/lib/api/payment-page.schema";
import type { Dictionary, Locale } from "@/app/[lang]/dictionaries";
import { useCart } from "@/hooks/use-cart";
import { usePageQuote } from "@/hooks/use-market-quote";
import { domesticMarket, findMarket, previewFromQuote } from "@/lib/markets";
import { trackPageView } from "@/lib/tracking";
import { initMetaPixel, trackMetaEvent } from "@/lib/meta-pixel";
import { trackAffiliateClick } from "@/lib/api/payment-page";
import { Button } from "@/components/ui/button";
import { Globe } from "lucide-react";
import Header from "@/app/[lang]/checkout/[token]/_components/header";
import { BrandTheme } from "@/components/brand-theme";
import { CheckoutLayout } from "@/app/[lang]/checkout/_components/checkout-layout";
import { SecuredBy } from "@/app/[lang]/checkout/[token]/_components/secured-by";
import { PageOrderSummary } from "./page-order-summary";
import { PagePaymentForm } from "./page-payment-form";

const localeLabels: Record<Locale, string> = {
  en: "SW",
  sw: "EN",
};

interface PageShellProps {
  slug: string;
  pageData: PaymentPageData;
  defaultLocale: Locale;
  dictionaries: Record<Locale, Dictionary>;
  meta?: string;
  affiliateCode?: string;
  /** The session a payer coming back from a card page is waiting on. */
  returnReference?: string;
}

export function PageShell({
  slug,
  pageData,
  defaultLocale,
  dictionaries,
  meta,
  affiliateCode,
  returnReference,
}: PageShellProps) {
  const cart = useCart();
  const [locale, setLocale] = useState<Locale>(defaultLocale);
  // Market the customer is previewing prices for; null = the merchant's own.
  // Follows the payer's phone number once they type one.
  const [previewCountry, setPreviewCountry] = useState<string | null>(null);

  const allowCustomAmount =
    pageData.page_type === "fixed" &&
    pageData.allow_custom_amount === true &&
    pageData.min_amount != null &&
    pageData.max_amount != null;

  const initialCustomAmount = useMemo(() => {
    if (!allowCustomAmount) return 0;
    const min = pageData.min_amount ?? 0;
    const suggested = pageData.amount;
    if (suggested > 0 && suggested >= min && suggested <= (pageData.max_amount ?? Infinity)) {
      return suggested;
    }
    return min;
  }, [allowCustomAmount, pageData.amount, pageData.min_amount, pageData.max_amount]);

  const [customAmount, setCustomAmount] = useState(initialCustomAmount);

  // Auto-select single non-tracked item into cart
  useEffect(() => {
    if (pageData.page_type !== "catalog" || !pageData.items) return;
    const activeItems = pageData.items.filter((i) => i.is_active);
    if (activeItems.length === 1 && !activeItems[0].track_quantity) {
      cart.addItem(activeItems[0]);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Track page view (no-op when the inline early-tracking script already did)
  useEffect(() => {
    trackPageView(slug);
  }, [slug]);

  // Merchant's Meta Pixel: PageView + ViewContent
  useEffect(() => {
    if (!pageData.meta_pixel_id) return;
    initMetaPixel(pageData.meta_pixel_id);
    trackMetaEvent("ViewContent", {
      content_name: pageData.title,
      ...(pageData.amount > 0
        ? { value: pageData.amount, currency: pageData.currency || "TZS" }
        : {}),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageData.meta_pixel_id]);

  // Track affiliate click
  useEffect(() => {
    if (affiliateCode) {
      trackAffiliateClick(affiliateCode);
    }
  }, [affiliateCode]);

  const effectiveAmount =
    pageData.page_type === "catalog"
      ? cart.totalAmount
      : allowCustomAmount
        ? customAmount
        : pageData.amount;

  // ---- Market preview (indicative; the checkout response is binding) ----
  const domestic = domesticMarket(pageData.markets, pageData.currency);
  const previewMarket =
    findMarket(pageData.markets, previewCountry) ?? domestic;
  const isForeignPreview =
    !!previewMarket && !!domestic && previewMarket.country !== domestic.country;
  // Convert using the live total, or the dearest item while the cart is empty
  const referenceAmount =
    effectiveAmount > 0
      ? effectiveAmount
      : Math.max(
          pageData.amount ?? 0,
          ...(pageData.items ?? [])
            .filter((i) => i.is_active && i.currency === pageData.currency)
            .map((i) => i.price),
        );
  const previewQuote = usePageQuote(
    slug,
    { country: previewMarket?.country, amount: referenceAmount },
    isForeignPreview,
  );
  const preview = isForeignPreview ? previewFromQuote(previewQuote.data) : null;

  const handlePayerCountryChange = useCallback(
    (country: string | null) => {
      if (country && findMarket(pageData.markets, country)) {
        setPreviewCountry(country);
      }
    },
    [pageData.markets],
  );

  const focusPhoneInput = useCallback(() => {
    const input = document.querySelector<HTMLInputElement>(
      "#payment-form [data-slot='phone-input']",
    );
    if (input) {
      input.scrollIntoView({ behavior: "smooth", block: "center" });
      input.focus();
    }
  }, []);

  const dictionary = dictionaries[locale];
  const profile = pageData.profile;
  const merchantName = profile?.merchant_name || "Merchant";
  // The API offers only the methods this merchant can take
  const allowedMethods = profile?.allowed_methods?.length
    ? profile.allowed_methods
    : ["mobile_money" as const];

  return (
    <BrandTheme brandColor={profile?.brand_color}>
      <CheckoutLayout
        header={
          <div className="flex items-center justify-between">
            <Header
              name={merchantName}
              logo={profile?.merchant_logo ?? undefined}
            />
            <Button
              variant="ghost"
              size="sm"
              className="h-9 gap-1.5 text-muted-foreground"
              onClick={() => setLocale(locale === "en" ? "sw" : "en")}
              aria-label={`Switch to ${localeLabels[locale]}`}
            >
              <Globe className="size-4" />
              {localeLabels[locale]}
            </Button>
          </div>
        }
        summary={
          <PageOrderSummary
            pageData={pageData}
            cart={cart}
            dictionary={dictionary}
            customAmount={allowCustomAmount ? customAmount : undefined}
            onCustomAmountChange={allowCustomAmount ? setCustomAmount : undefined}
            onAmountSubmit={allowCustomAmount ? focusPhoneInput : undefined}
            previewMarket={previewMarket}
            onPreviewMarketChange={setPreviewCountry}
            preview={preview}
          />
        }
      >
        <PagePaymentForm
          slug={slug}
          pageData={pageData}
          cart={cart}
          allowedMethods={allowedMethods}
          dictionary={dictionary}
          effectiveAmount={effectiveAmount}
          preview={preview}
          onPayerCountryChange={handlePayerCountryChange}
          allowCustomAmount={allowCustomAmount}
          customAmount={customAmount}
          minAmount={pageData.min_amount ?? 0}
          maxAmount={pageData.max_amount ?? 0}
          meta={meta}
          affiliateCode={affiliateCode}
          returnReference={returnReference}
        />
        <SecuredBy className="mt-8" dictionary={dictionary} />
      </CheckoutLayout>
    </BrandTheme>
  );
}
